import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronRight, ArrowDown } from "lucide-react";
import AnimatedTextCycle from "@/components/ui/animated-text-cycle";
import { useHeroBackground } from "@/hooks/useHeroBackground";
import { YouTubePlayer } from "@/components/youtube/YouTubePlayer";
import { useIsMobile } from "@/hooks/use-mobile";

interface HeroSectionProps {
  scrollToSection: (id: string) => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ scrollToSection }) => {
  const navigate = useNavigate();
  const { settings, loading } = useHeroBackground();
  const isMobile = useIsMobile();
  
  // Debug logs
  React.useEffect(() => {
    console.log('🎨 [HeroSection] Settings:', settings);
    console.log('🎨 [HeroSection] Loading:', loading);
    console.log('🎨 [HeroSection] Background URL:', settings?.background_image_url);
    console.log('🎨 [HeroSection] YouTube Embed:', settings?.youtube_video_url);
    console.log('🎨 [HeroSection] Overlay Opacity:', settings?.overlay_opacity);
    console.log('📱 [HeroSection] Is Mobile:', isMobile);
  }, [settings, loading, isMobile]);
  
  // Decide se mostra vídeo ou imagem
  const showVideo = settings?.youtube_video_url;
  const showImage = !showVideo; // Sempre mostra imagem quando não há vídeo
  
  // Verifica se deve aplicar o degradê (quando há imagem de fundo)
  const shouldShowGradient = settings?.background_image_url && !loading;
  
  return (
    <section className="relative pt-28 lg:pt-40 pb-16 md:pb-24 px-4 overflow-hidden">
      {/* Fallback background effects quando não há imagem */}
      {!settings?.background_image_url && (
        <>
          {/* Background glow effects */}
          <div className="absolute top-32 left-1/4 w-72 h-72 bg-[#00B6F0] opacity-20 blur-[100px] rounded-full -z-10"></div>
          <div className="absolute top-48 right-1/4 w-96 h-96 bg-[#8B5CF6] opacity-10 blur-[120px] rounded-full -z-10"></div>
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-center opacity-20 -z-10"></div>
        </>
      )}

      {/* DEGRADÊ ULTRA EXTENSO - começando do meio da seção para baixo */}
      {shouldShowGradient && (
        <div 
          className="absolute inset-0 w-full pointer-events-none"
          style={{
            background: `linear-gradient(
              to bottom, 
              transparent 0%, 
              transparent 40%, 
              rgba(12, 17, 23, 0.005) 42%, 
              rgba(12, 17, 23, 0.01) 44%, 
              rgba(12, 17, 23, 0.02) 46%, 
              rgba(12, 17, 23, 0.04) 48%, 
              rgba(12, 17, 23, 0.06) 50%, 
              rgba(12, 17, 23, 0.09) 52%, 
              rgba(12, 17, 23, 0.12) 54%, 
              rgba(12, 17, 23, 0.16) 56%, 
              rgba(12, 17, 23, 0.21) 58%, 
              rgba(12, 17, 23, 0.26) 60%, 
              rgba(12, 17, 23, 0.32) 62%, 
              rgba(12, 17, 23, 0.38) 64%, 
              rgba(12, 17, 23, 0.45) 66%, 
              rgba(12, 17, 23, 0.52) 68%, 
              rgba(12, 17, 23, 0.59) 70%, 
              rgba(12, 17, 23, 0.66) 72%, 
              rgba(12, 17, 23, 0.73) 74%, 
              rgba(12, 17, 23, 0.79) 76%, 
              rgba(12, 17, 23, 0.84) 78%, 
              rgba(12, 17, 23, 0.88) 80%, 
              rgba(12, 17, 23, 0.92) 82%, 
              rgba(12, 17, 23, 0.95) 84%, 
              rgba(12, 17, 23, 0.97) 86%, 
              rgba(12, 17, 23, 0.98) 88%, 
              rgba(12, 17, 23, 0.99) 90%, 
              rgba(12, 17, 23, 0.995) 92%, 
              rgba(12, 17, 23, 0.998) 95%, 
              rgba(12, 17, 23, 0.999) 98%, 
              rgba(12, 17, 23, 1) 100%
            )`,
            zIndex: 5,
          }}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12">
          <div className="w-full md:w-1/2">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left relative z-10"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight min-h-[1.2em]">
                <span className="text-white block drop-shadow-lg">
                  Sua Jornada{" "}
                </span>
                <span className="block">
                  <span className="text-white drop-shadow-lg">Pró começa </span>
                  <AnimatedTextCycle 
                    words={["no Arena!", "por aqui", "por você!"]}
                    interval={3000}
                    className="text-transparent bg-clip-text bg-gradient-to-br from-[#008DCA] to-[#00B6F0] drop-shadow-lg"
                  />
                </span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-gray-200 mb-8 text-center md:text-left drop-shadow-md">
                Conecte-se com os melhores <span className="text-[#00B6F0] font-semibold">Pro Players</span> do seu jogo preferido e melhore sua play de forma acelerada!
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start relative z-10">
                <Button 
                  onClick={() => navigate("/mentores")} 
                  size="lg" 
                  className="bg-gradient-to-r from-[#00B6F0] to-[#0077B6] text-black font-bold hover:opacity-90 hover:scale-105 transition-all shadow-lg"
                >
                  Encontrar Mentor
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  onClick={() => navigate("/cadastro")} 
                  size="lg" 
                  variant="outline" 
                  className="border-[#00B6F0] text-[#00B6F0] hover:bg-[#00B6F0]/10 hover:text-[#00B6F0] backdrop-blur-sm bg-white/10 shadow-lg"
                >
                  Quero ser Pro Player
                </Button>
              </div>

              <div className="mt-12 hidden md:flex items-center gap-3 relative z-10">
                <button 
                  onClick={() => scrollToSection('como-funciona-section')} 
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors drop-shadow-md"
                >
                  Conhecer mais <ArrowDown className="h-4 w-4 animate-bounce" />
                </button>
              </div>
            </motion.div>
          </div>

          <motion.div 
            className="w-full md:w-1/2 flex-shrink-0"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative max-w-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-[#00B6F0]/40 to-[#8B5CF6]/40 blur-xl rounded-full"></div>
              
              {/* Vídeo do YouTube quando configurado */}
              {showVideo && (
                <div className="relative z-10">
                  <YouTubePlayer 
                    embedCode={settings.youtube_video_url} 
                    className="shadow-2xl border-2 border-[#008DCA] rounded-lg"
                  />
                </div>
              )}
              
              {/* Imagem padrão quando não há vídeo */}
              {showImage && (
                <img 
                  src="/lovable-uploads/cfff68c2-f717-4aa8-83fd-4c6ec6f57ba2.jpg" 
                  alt="Arena Pro Player" 
                  className="w-full h-auto object-cover rounded-xl relative z-10 shadow-2xl max-w-full hero-image"
                  loading="eager"
                  onError={(e) => {
                    console.error('❌ Erro ao carregar imagem do hero:', e);
                    e.currentTarget.src = "https://placehold.co/600x400/252B36/00B6F0?text=ARENA+PRO+PLAYER";
                  }}
                  onLoad={() => {
                    console.log('✅ Imagem do hero carregada com sucesso');
                  }}
                />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
